/**
 * CareLogix™ ORACLE — Data Mode Badge™ (additive).
 *
 * Visually surfaces the live-data envelope on every ORACLE answer card,
 * LOGIX-2 response, Sports/Market/Grant/Contract/Research output. Renders
 * exactly what the backend reports — never embellishes.
 */
import React from "react";

const MODE = {
  LIVE_VERIFIED:                { icon: "🟢", label: "LIVE VERIFIED",                color: "#7AC860", bg: "rgba(122,200,96,0.10)" },
  LIVE_DELAYED:                 { icon: "🟡", label: "LIVE DELAYED",                 color: "#fcd34d", bg: "rgba(252,211,77,0.10)" },
  KNOWLEDGE_ONLY:               { icon: "🟡", label: "KNOWLEDGE ONLY",               color: "#fcd34d", bg: "rgba(252,211,77,0.10)" },
  // Phase 40 — customer-facing badge labels never leak internal status strings.
  // Operator surfaces (AdminOne) get the raw label via the technical view.
  PROVIDER_NOT_CONFIGURED:      { icon: "🟡", label: "COMING SOON",                  color: "#fcd34d", bg: "rgba(252,211,77,0.10)" },
  SOURCE_UNAVAILABLE:           { icon: "🟡", label: "PREVIEW",                      color: "#fcd34d", bg: "rgba(252,211,77,0.10)" },
  ERROR:                        { icon: "🟡", label: "TEMPORARILY UNAVAILABLE",      color: "#fcd34d", bg: "rgba(252,211,77,0.10)" },
  STUB_AWAITING_IMPLEMENTATION: { icon: "🟡", label: "COMING SOON",                  color: "#fcd34d", bg: "rgba(252,211,77,0.10)" },
};

const S = {
  wrap: {
    display:       "inline-flex",
    flexDirection: "column",
    gap:           4,
    padding:       "8px 12px",
    border:        "1px solid",
    borderRadius:  10,
    fontFamily:    "'Space Mono', monospace",
    fontSize:      11,
    lineHeight:    1.35,
    maxWidth:      520,
  },
  row1:  { display: "flex", alignItems: "center", gap: 8, fontWeight: 700, letterSpacing: "0.10em" },
  meta:  { color: "#cbd5e1", fontSize: 10, fontFamily: "'Inter', system-ui, sans-serif", letterSpacing: 0 },
  link:  { color: "#c5a059", textDecoration: "underline" },
  notice:{ color: "#cbd5e1", fontSize: 10.5, fontFamily: "'Inter', system-ui, sans-serif", letterSpacing: 0, marginTop: 2 },
};

function _fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function OracleDataModeBadge({ envelope, compact = false, testid = "oracle-data-mode-badge" }) {
  if (!envelope || !envelope.data_mode) return null;
  const cfg = MODE[envelope.data_mode] || MODE.ERROR;
  return (
    <div
      data-testid={testid}
      data-mode={envelope.data_mode}
      role="status"
      aria-label={`ORACLE data mode: ${cfg.label}`}
      style={{ ...S.wrap, color: cfg.color, borderColor: cfg.color, background: cfg.bg }}
    >
      <div style={S.row1}>
        <span aria-hidden="true">{cfg.icon}</span>
        <span>{cfg.label}</span>
        {envelope.domain ? (
          <span style={{ color: "#94a3b8", fontWeight: 500, letterSpacing: "0.06em" }}>· {envelope.domain}</span>
        ) : null}
      </div>
      {!compact && (
        <>
          {envelope.source_name ? (
            <div style={S.meta}>
              Source:{" "}
              {envelope.source_url ? (
                <a href={envelope.source_url} target="_blank" rel="noopener noreferrer" style={S.link}>{envelope.source_name}</a>
              ) : envelope.source_name}
            </div>
          ) : (
            <div style={S.meta}>Source: —</div>
          )}
          <div style={S.meta}>
            Verified: {_fmt(envelope.last_verified_at) === "—" ? <em style={{ color: "#fcd34d" }}>not verified</em> : _fmt(envelope.last_verified_at)}
            {typeof envelope.confidence === "number" && envelope.confidence > 0
              ? <>  · Confidence: {Math.round(envelope.confidence * 100)}%</>
              : null}
          </div>
          {envelope.truthfulness_notice ? (
            <div style={S.notice}>{envelope.truthfulness_notice}</div>
          ) : null}
        </>
      )}
    </div>
  );
}
